import { useState, useEffect, useCallback } from 'react';
import {
  tenantService,
  Tenant,
  TenantFilters,
  CreateTenantDto,
  UpdateTenantDto,
  BoTenantStatus,
  ImpersonateTenantResponse,
  ExtendTrialResponse,
  ChangePlanResponse,
} from '../services';
import { PAGINATION, TenantStatus } from '@erpgold/shared';

interface UseTenantsReturn {
  tenants: Tenant[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: TenantFilters;
  setFilters: (filters: TenantFilters) => void;
  refetch: () => void;
  createTenant: (data: CreateTenantDto) => Promise<Tenant>;
  updateTenant: (id: string, data: UpdateTenantDto) => Promise<Tenant>;
  suspendTenant: (id: string, reason: string) => Promise<void>;
  activateTenant: (id: string) => Promise<void>;
  extendTrial: (id: string, days: number) => Promise<ExtendTrialResponse>;
  changePlan: (id: string, planId: string) => Promise<ChangePlanResponse>;
  impersonateTenant: (id: string) => Promise<ImpersonateTenantResponse>;
  deleteTenant: (id: string) => Promise<void>;
}

export const useTenants = (initialFilters: TenantFilters = {}): UseTenantsReturn => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<TenantFilters>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
  });

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantService.getTenants(filters);
      setTenants(response.data);
      setMeta({
        page: response.meta.page,
        limit: response.meta.limit,
        total: response.meta.total,
        totalPages: response.meta.total_pages,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch tenants'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const createTenant = async (data: CreateTenantDto): Promise<Tenant> => {
    const tenant = await tenantService.createTenant(data);
    await fetchTenants();
    return tenant;
  };

  const updateTenant = async (id: string, data: UpdateTenantDto): Promise<Tenant> => {
    const tenant = await tenantService.updateTenant(id, data);
    setTenants((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...tenant } : t))
    );
    return tenant;
  };

  const suspendTenant = async (id: string, reason: string): Promise<void> => {
    await tenantService.suspendTenant(id, reason);
    setTenants((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status: TenantStatus.SUSPENDED as BoTenantStatus } : t))
    );
  };

  const activateTenant = async (id: string): Promise<void> => {
    await tenantService.activateTenant(id);
    setTenants((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status: TenantStatus.ACTIVE as BoTenantStatus } : t))
    );
  };

  const impersonateTenant = async (id: string): Promise<ImpersonateTenantResponse> => {
    return tenantService.impersonateTenant(id);
  };

  const extendTrial = async (id: string, days: number): Promise<ExtendTrialResponse> => {
    const result = await tenantService.extendTrial(id, days);
    await fetchTenants();
    return result;
  };

  const changePlan = async (id: string, planId: string): Promise<ChangePlanResponse> => {
    const result = await tenantService.changePlan(id, planId);
    setTenants((prev) =>
      prev.map((t) => (t.id === id ? { ...t, plan: planId as Tenant['plan'] } : t))
    );
    return result;
  };

  const deleteTenant = async (id: string): Promise<void> => {
    await tenantService.deleteTenant(id);
    setTenants((prev) => prev.filter((t) => t.id !== id));
    setMeta((prev) => ({ ...prev, total: prev.total - 1 }));
  };

  return {
    tenants,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchTenants,
    createTenant,
    updateTenant,
    suspendTenant,
    activateTenant,
    extendTrial,
    changePlan,
    impersonateTenant,
    deleteTenant,
  };
};

interface UseTenantDetailReturn {
  tenant: Tenant | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  updateTenant: (data: UpdateTenantDto) => Promise<Tenant>;
}

export const useTenantDetail = (id: string): UseTenantDetailReturn => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTenant = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await tenantService.getTenantById(id);
      setTenant(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch tenant'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  const updateTenant = async (data: UpdateTenantDto): Promise<Tenant> => {
    const updated = await tenantService.updateTenant(id, data);
    setTenant(updated);
    return updated;
  };

  return {
    tenant,
    loading,
    error,
    refetch: fetchTenant,
    updateTenant,
  };
};

interface UseTenantStatsReturn {
  stats: {
    revenue: number;
    orders: number;
    users: number;
    stores: number;
    products: number;
    storage_used: number;
    last_login_at?: string;
    last_order_at?: string;
    last_activity: string;
  } | null;
  loading: boolean;
  error: Error | null;
}

export const useTenantStats = (id: string): UseTenantStatsReturn => {
  const [stats, setStats] = useState<{
    revenue: number;
    orders: number;
    users: number;
    stores: number;
    products: number;
    storage_used: number;
    last_login_at?: string;
    last_order_at?: string;
    last_activity: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) return;
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await tenantService.getTenantStats(id);
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch stats'));
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [id]);

  return { stats, loading, error };
};
